import React, { useMemo, useState } from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Input, Select } from "antd";
import { JOURNAL_STATUS_META, STATUS } from "./doc-exp-utils";
import searchSrc from "./assets/search.svg";

const KIND = {
  STATUS: "idStatus",
  VID: "vidPutevki",
  BRANCH: "idBranch",
  PAY: "pay"
};

const KIND_ITEMS = [
  { key: KIND.STATUS, label: "Статус" },
  { key: KIND.VID, label: "Вид путёвки" },
  { key: KIND.BRANCH, label: "Филиал" },
  { key: KIND.PAY, label: "Оплата" }
];

const STATUS_VALUE_OPTIONS = [
  { value: STATUS.NEW, label: JOURNAL_STATUS_META[STATUS.NEW].label },
  { value: STATUS.CONFIRMED, label: JOURNAL_STATUS_META[STATUS.CONFIRMED].label },
  { value: STATUS.CHECKED_IN, label: JOURNAL_STATUS_META[STATUS.CHECKED_IN].label },
  { value: STATUS.CHECKED_OUT, label: JOURNAL_STATUS_META[STATUS.CHECKED_OUT].label },
  { value: STATUS.CANCELLED, label: JOURNAL_STATUS_META[STATUS.CANCELLED].label }
];

const PAY_VALUE_OPTIONS = [
  { value: "paid", label: "Оплаченные" },
  { value: "unpaid", label: "Неоплаченные" }
];

const isActive = (kind, value) => {
  if (kind === KIND.STATUS) return value != null && value !== "";
  if (kind === KIND.PAY) return value === "paid" || value === "unpaid";
  return Number(value) > 0;
};

const emptyFor = (kind) => {
  if (kind === KIND.STATUS || kind === KIND.PAY) return null;
  return 0;
};

const labelOf = (options, raw) => {
  const found = (options || []).find((o) => String(o.value) === String(raw));
  return found?.label ?? String(raw);
};

const MiniSelect = ({ autoFocus, defaultOpen, disabled, options, placeholder, value, onChange, width }) => (
  <Select
    autoFocus={autoFocus}
    defaultOpen={defaultOpen}
    disabled={disabled}
    showSearch
    optionFilterProp="label"
    placeholder={placeholder}
    value={value}
    options={options}
    popupMatchSelectWidth={false}
    variant="borderless"
    suffixIcon={null}
    style={{ width }}
    onChange={onChange}
  />
);

/**
 * Поиск и чипы фильтров журнала: статус / вид / филиал / оплата.
 */
const DocExpsComboFilter = ({ value, branches, vidPutevki, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [draftKind, setDraftKind] = useState(null);
  const [editKind, setEditKind] = useState(null);

  const vidOptions = useMemo(
    () => (vidPutevki || []).filter((o) => o.value !== 0),
    [vidPutevki]
  );
  const branchOptions = useMemo(
    () => (branches || []).filter((b) => b.value !== 0),
    [branches]
  );

  const valueOptionsByKind = useMemo(
    () => ({
      [KIND.STATUS]: STATUS_VALUE_OPTIONS,
      [KIND.VID]: vidOptions,
      [KIND.BRANCH]: branchOptions,
      [KIND.PAY]: PAY_VALUE_OPTIONS
    }),
    [vidOptions, branchOptions]
  );

  const chips = useMemo(() => {
    const items = [];
    if (isActive(KIND.STATUS, value.idStatus)) {
      items.push({
        key: KIND.STATUS,
        kindLabel: "Статус",
        valueLabel: labelOf(STATUS_VALUE_OPTIONS, value.idStatus)
      });
    }
    if (isActive(KIND.VID, value.vidPutevki)) {
      items.push({
        key: KIND.VID,
        kindLabel: "Вид",
        valueLabel: labelOf(vidOptions, value.vidPutevki)
      });
    }
    if (isActive(KIND.BRANCH, value.idBranch)) {
      items.push({
        key: KIND.BRANCH,
        kindLabel: "Филиал",
        valueLabel: labelOf(branchOptions, value.idBranch)
      });
    }
    if (isActive(KIND.PAY, value.pay)) {
      items.push({
        key: KIND.PAY,
        kindLabel: "Оплата",
        valueLabel: labelOf(PAY_VALUE_OPTIONS, value.pay)
      });
    }
    return items;
  }, [value.idStatus, value.vidPutevki, value.idBranch, value.pay, vidOptions, branchOptions]);

  const used = new Set(chips.map((c) => c.key));
  const availableKinds = KIND_ITEMS.filter((k) => !used.has(k.key)).map((k) => ({
    value: k.key,
    label: k.label
  }));
  const canAdd = availableKinds.length > 0;

  const applyKindValue = (kind, nextValue) => {
    if (kind === KIND.STATUS) {
      onChange({
        idStatus: nextValue,
        hideCancelled: nextValue !== STATUS.CANCELLED
      });
      return;
    }
    if (kind === KIND.VID) {
      onChange({ vidPutevki: nextValue || 0 });
      return;
    }
    if (kind === KIND.PAY) {
      onChange({ pay: nextValue === "paid" || nextValue === "unpaid" ? nextValue : null });
      return;
    }
    onChange({ idBranch: nextValue || 0 });
  };

  const clearKind = (kind) => {
    applyKindValue(kind, emptyFor(kind));
    if (editKind === kind) setEditKind(null);
  };

  const startAdd = () => {
    setEditKind(null);
    setDraftKind(null);
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setDraftKind(null);
  };

  const pickDraftValue = (next) => {
    if (!draftKind) return;
    applyKindValue(draftKind, next);
    cancelAdd();
  };

  return (
    <div className="doc-exps-combo" role="group" aria-label="Поиск и фильтры журнала">
      <span className="doc-exps-combo__search-icon">
        <img src={searchSrc} alt="" />
      </span>
      <Input
        variant="borderless"
        allowClear
        className="doc-exps-combo__search-input"
        value={value.q || ""}
        placeholder="ФИО, телефон, почта, номер путёвки..."
        aria-label="Поиск"
        onChange={(e) => onChange({ q: e.target.value })}
      />
      {chips.map((chip) =>
        editKind === chip.key ? (
          <span key={chip.key} className="doc-exps-combo__draft">
            <span className="doc-exps-combo__chip-kind">{chip.kindLabel}</span>
            <MiniSelect
              autoFocus
              defaultOpen
              options={valueOptionsByKind[chip.key]}
              placeholder="Значение"
              value={
                chip.key === KIND.STATUS
                  ? value.idStatus
                  : chip.key === KIND.PAY
                    ? value.pay
                    : value[chip.key]
              }
              width={140}
              onChange={(next) => {
                applyKindValue(chip.key, next);
                setEditKind(null);
              }}
            />
            <button
              type="button"
              className="doc-exps-combo__chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKind(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.key} className="doc-exps-combo__chip">
            <button
              type="button"
              className="doc-exps-combo__chip-btn"
              title="Изменить значение"
              onClick={() => {
                cancelAdd();
                setEditKind(chip.key);
              }}
            >
              <span className="doc-exps-combo__chip-kind">{chip.kindLabel}</span>
              <span className="doc-exps-combo__chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="doc-exps-combo__chip-clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={() => clearKind(chip.key)}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="doc-exps-combo__draft">
          <MiniSelect
            autoFocus={!draftKind}
            defaultOpen={!draftKind}
            options={availableKinds}
            placeholder="Вид фильтра"
            value={draftKind}
            width={130}
            onChange={(kind) => setDraftKind(kind)}
          />
          <MiniSelect
            key={draftKind || "value"}
            autoFocus={Boolean(draftKind)}
            defaultOpen={Boolean(draftKind)}
            disabled={!draftKind}
            options={draftKind ? valueOptionsByKind[draftKind] || [] : []}
            placeholder="Значение"
            width={140}
            onChange={pickDraftValue}
          />
          <button
            type="button"
            className="doc-exps-combo__chip-clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding ? (
        <button
          type="button"
          className="doc-exps-combo__add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

export default DocExpsComboFilter;
